import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useStartCommissionRecalculation } from 'hooks/useStartCommissionRecalculation';
import { CommissionRecalculationTaskListFilter } from 'pages/Finance/CommissionRecalculationTask/CommissionRecalculationTaskList/CommissionRecalculationTaskListFilter/CommissionRecalculationTaskListFilter';
import { CommissionRecalculationTaskListFilterChips } from 'pages/Finance/CommissionRecalculationTask/CommissionRecalculationTaskList/CommissionRecalculationTaskListFilter/CommissionRecalculationTaskListFilterChips';
import { StatusField } from 'pages/Finance/CommissionRecalculationTask/CommissionRecalculationTaskList/components/StatusField/StatusField';
import { queryClient } from 'providers/queryClient';
import { RecordContextProvider, useListContext, useNotify } from 'react-admin';
import { DefaultValues, FormProvider } from 'react-hook-form';
import { DateRangePickerValue } from 'shared/mui/DatePicker/DateRangePicker/DateRangePicker';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { useSyncFilter } from 'shared/mui/Filter/hooks/useSyncFilter';
import {
  ChipField,
  CurrencyField,
  DateTimeField,
  EmbeddedFunctionField,
  FunctionField,
  SingleFieldList,
  TextField,
} from 'shared/react-admin/Fields';
import { PeriodTzActivityField } from 'shared/react-admin/Fields/PeriodTzActivityField/PeriodTzActivityField';
import { ReferenceArrayField } from 'shared/react-admin/ReferenceFields';
import { CommissionsTaskLaunchDialog } from 'shared/widgets/CommissionsTaskLaunchDialog/CommissionsTaskLaunchDialog';
import { Commission } from 'types/embedded/commission';
import { CommissionRecalculationTask } from 'types/embedded/commissionRecalculationTask';
import { Currency } from 'types/embedded/currency';
import { convertReactAdminIdToHydraId, getLastUrlSegment } from 'utils';
import { cleanEmpty } from 'utils/cleanEmpty';
import { formatInputToTimestamp } from 'utils/date';

import { DatagridConfigurableStyled } from './CommissionRecalculationTaskListContent.styled';

type CommissionRecalculationTaskFilterValues = {
  appliedDate: [DateRangePickerValue, DateRangePickerValue];
  startedAt?: number;
  completedAt?: number;
  'commission.provider'?: string[];
  'commission.merchant'?: string[];
  merchant: string[];
  provider: string[];
  status: string | undefined;
  commission: string[];
};

const DEFAULT_FORM_VALUES: CommissionRecalculationTaskFilterValues = {
  status: undefined,
  appliedDate: [null, null],
  merchant: [],
  provider: [],
  commission: [],
};

export const CommissionRecalculationTaskListContent: React.FC = () => {
  const notify = useNotify();
  const { filterValues } = useListContext();

  const { hasResourceAccess } = usePermissions();
  const {
    record: recordConfirm,
    handleOpenRecordModal: handleOpenConfirmModal,
    handleCloseRecordModal: handleCloseConfirmModal,
    openRecordModal: openConfirmModal,
  } = useRecordModal<CommissionRecalculationTask>();

  const { form, onSubmit, appliedFilters, onReset, setAppliedFilter, openDrawer, toggleDrawer } =
    useFilters<CommissionRecalculationTaskFilterValues>({
      mode: 'all',
      defaultValues: {
        status: filterValues?.status
          ? convertReactAdminIdToHydraId(filterValues?.status, 'commission_recalculation_statuses')
          : undefined,
        appliedDate: [
          formatInputToTimestamp(filterValues?.['recalculateFrom[gte]']),
          formatInputToTimestamp(filterValues?.['recalculateFrom[lte]']),
        ],
        merchant: filterValues?.merchant || [],
        provider: filterValues?.provider || [],
        commission: filterValues?.commission || [],
      },
      resetValues: DEFAULT_FORM_VALUES,
    });

  useSyncFilter<CommissionRecalculationTaskFilterValues>({
    appliedFilters,
    transform: (
      filterValues: DefaultValues<CommissionRecalculationTaskFilterValues>,
    ): CommissionRecalculationTaskFilterValues => {
      const {
        appliedDate = [null, null],
        merchant = [],
        provider = [],
        status,
        ...restFilters
      } = filterValues;

      return cleanEmpty({
        ...restFilters,
        appliedDate,
        'commission.provider': provider,
        'commission.merchant': merchant,
        status: status ? getLastUrlSegment(status) : undefined,
        merchant,
        provider,
        ['recalculateFrom[gte]']: formatInputToTimestamp(appliedDate?.[0], { unit: 'seconds' }),
        ['recalculateFrom[lte]']: formatInputToTimestamp(appliedDate?.[1], { unit: 'seconds' }),
      });
    },
  });

  const { mutateAsync: startRecalculation, isPending } = useStartCommissionRecalculation();

  const onRecalculation = async () => {
    const taskId = getLastUrlSegment(recordConfirm?.id);
    const commissionId = recordConfirm?.commission;

    if (taskId && commissionId) {
      try {
        await startRecalculation(taskId);
        const commissionId = recordConfirm?.commission;
        const commissionStatus = recordConfirm?.status;

        queryClient.invalidateQueries({ queryKey: [Resources.CommissionRecalculationTasks] });
        queryClient.invalidateQueries({
          queryKey: [Resources.CommissionRecalculationTasks, taskId],
        });
        queryClient.invalidateQueries({ queryKey: [Resources.Commissions, commissionId] });
        queryClient.invalidateQueries({ queryKey: ['commission_recalculation_statuses'] });
        queryClient.invalidateQueries({
          queryKey: ['commission_recalculation_statuses', commissionStatus],
        });

        handleCloseConfirmModal();
      } catch (error: any) {
        notify(error.data?.detail, { type: 'error' });
      }
    }
  };

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton key="select-column" />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <CommissionRecalculationTaskListFilter />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label="Очистить" onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <CommissionRecalculationTaskListFilterChips />
            <FilterClearButton key="clear-button" onClick={onReset} visible={true} />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
      <DatagridConfigurableStyled bulkActionButtons={false}>
        <TextField label="ID" sortBy="id" source="originId" />
        {hasResourceAccess(Resources.Currencies) && (
          <EmbeddedFunctionField
            cellClassName="column-commission task"
            label="Задание по комиссии"
            linkConfig={{
              resource: Resources.Commissions,
              idField: 'commission.@id',
            }}
            sortable={false}
            sourceField="commission.name"
          />
        )}
        <PeriodTzActivityField
          cellClassName="column-recalculate"
          endDateSource="recalculateTo"
          label="Период перерасчёта"
          sortable={false}
          startDateSource="recalculateFrom"
        />
        <FunctionField
          cellClassName="state"
          label="Состояние"
          render={(record: CommissionRecalculationTask) => {
            return <StatusField onRecalculation={handleOpenConfirmModal} record={record} />;
          }}
        />
        {hasResourceAccess(Resources.Providers) && (
          <EmbeddedFunctionField
            cellClassName="column-commission provider"
            label="Провайдер"
            sortBy="commission.provider.id"
            sourceField="commission.provider.name"
          />
        )}
        {hasResourceAccess('merchants') && (
          <EmbeddedFunctionField
            cellClassName="column-commission merchant"
            label="Мерчант"
            sortBy="commission.merchant.id"
            sourceField="commission.merchant.name"
          />
        )}
        {hasResourceAccess(Resources.Currencies) && (
          <EmbeddedFunctionField
            cellClassName="column-commission currency"
            label="Валюта"
            render={(value: Currency) => {
              return (
                <RecordContextProvider value={value}>
                  <CurrencyField alphaCodeSource="alphaCode" nameSource="name" />
                </RecordContextProvider>
              );
            }}
            sortable={false}
            sourceField="commission.currency"
          />
        )}
        {hasResourceAccess(Resources.Currencies) && (
          <EmbeddedFunctionField
            cellClassName="column-commission transactionTypes"
            label="Типы транзакций"
            render={(value: Commission) => {
              return (
                <RecordContextProvider value={value}>
                  <ReferenceArrayField reference="transaction_types" source="transactionTypes">
                    <SingleFieldList linkType={false}>
                      <ChipField source="name" />
                    </SingleFieldList>
                  </ReferenceArrayField>
                </RecordContextProvider>
              );
            }}
            sortable={false}
            sourceField="commission"
          />
        )}

        <DateTimeField
          label={
            <>
              Дата запуска
              <br /> задания
            </>
          }
          sortable={false}
          source="startedAt"
        />
        <DateTimeField label="Дата завершения" sortable={false} source="completedAt" />
      </DatagridConfigurableStyled>
      <CommissionsTaskLaunchDialog
        isLoading={isPending}
        onClose={handleCloseConfirmModal}
        onConfirm={onRecalculation}
        open={openConfirmModal}
      />
    </>
  );
};
